import * as React from "react";
import {
    RichUtils,
    Editor,
    EditorState,
    Modifier,
    DraftHandleValue,
    ContentBlock,
    SelectionState,
    ContentState,
    BlockMap
} from "draft-js";

import {customInlineStyleMap, customStyleFn, blockStyleFn} from "./editorStateConverters";
import {DraftJSUtils} from "./utility";
import {FontFamily} from "shared/xp/models/widgets";
import {DraftStyles} from "./models";
import { OrderedSet } from "immutable";

interface TextEditorWidgetProps {
    editorState: EditorState;
    isSelected: boolean;
    readOnly: boolean;
    hasFakeHighlight: boolean;
    onEditorChange: (editorState: EditorState) => void;
    styleOverrides: OrderedSet<string>;
    fontProvider?: FontFamily;
    setRef: (ref: Editor) => void;
}



const deleteKeyCommands = ["delete", "delete-word", "backspace", "backspace-word", "backspace-to-start-of-line"];

class TextEditor extends React.Component<TextEditorWidgetProps> {
    onChange = (updatedEditorState: EditorState) => this.props.onEditorChange(updatedEditorState);

    handleKeyCommand = (command, editorState): DraftHandleValue => {
        const selection: SelectionState = editorState.getSelection();
        if(selection.isCollapsed() && deleteKeyCommands.indexOf(command) !== -1) {
            const content = editorState.getCurrentContent();
            const block = content.getBlockForKey(selection.getAnchorKey());
            const blockText = block.getText();
            if(blockText === DraftJSUtils.emptyBlockValue) {
                // There should be a better way to get this.
                const previousBlockKey =
                    editorState.getCurrentContent().getBlockBefore(editorState.getSelection().getAnchorKey()).getKey();
                const blockMap = content.getBlockMap().delete(block.getKey());
                const withoutPseudoEmptyBlock = content.merge({
                    blockMap,
                    selectionAfter: selection,
                });
                if(withoutPseudoEmptyBlock !== content) {
                    let nextEditorState = EditorState.push(
                        editorState,
                        withoutPseudoEmptyBlock,
                        "remove-range",
                    );

                    const nextContent = nextEditorState.getCurrentContent();
                    const nextContentBlock = nextContent.getBlockForKey(previousBlockKey);
                    nextEditorState = DraftJSUtils.forceFocusBlockEnding(nextEditorState, nextContentBlock);

                    this.onChange(nextEditorState);
                    return "handled";
                }
            }
        }
        // TODO: See which keyboard commands are built in and which we want to block.
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            this.onChange(newState);
            return "handled";
        }
        return "not-handled";
    }

    handleBeforeInput = (chars: string, editorState: EditorState): DraftHandleValue => {
        let nextEditorState = this.setEditorStateTextForSelection(editorState, chars);

        this.onChange(nextEditorState);

        return "handled";
    }

    setEditorStateTextForSelection = (editorState: EditorState, chars: string) => {
        const selection: SelectionState = editorState.getSelection();
        const content: ContentState = editorState.getCurrentContent();
        const block = content.getBlockForKey(selection.getAnchorKey());
        const blockHasText = block.getText().length > 0;
        const style = blockHasText ? editorState.getCurrentInlineStyle() : this.props.styleOverrides;
        const offset = selection.getStartOffset();
        const character = block.getCharacterList().get(selection.isCollapsed() ? offset : offset - 1);
        let entityKey = null;

        if (blockHasText && character) {
            entityKey = character.getEntity();
        }

        const textMethod = selection.isCollapsed() ? Modifier.insertText : Modifier.replaceText;
        const newContent = textMethod(content, selection, chars, style, entityKey);
        let nextEditorState = EditorState.push(editorState, newContent, "insert-characters");
        // This must happen AFTER we insert characters.
        if (!blockHasText) {
            const {fontProvider} = this.props;

            // This is newly created empty(!) content block.
            // with inserted text a few lines code above (please see Modifier.insertText)
            // but selection state, is from empty content block, so it has no seletions
            // Draft.js Entity should be applied to some selection. It can't be applied to empty selection
            // So we do the next:
            // 1) Select all newly inserted text charter(s)
            const currentContent = nextEditorState.getCurrentContent();
            const lastBlock = currentContent.getBlockMap().last();
            const lastBlockKey = lastBlock.getKey();

            const selection = new SelectionState({
                anchorKey: lastBlockKey,
                anchorOffset: 0,
                focusKey: lastBlockKey,
                focusOffset: 0,
            });

            nextEditorState = EditorState.forceSelection(
                nextEditorState,
                selection,
            );

            // 2) Apply fontProvider entity to the selection
            nextEditorState = DraftJSUtils.setFontMetaData(nextEditorState, fontProvider);

            const nextContent = nextEditorState.getCurrentContent();
            const nextContentBlock = nextContent.getBlockForKey(selection.getAnchorKey());
            // 3) Reset (or revert back) <Select all> selection from step 1, and move cursor and focus selection back
            nextEditorState = DraftJSUtils.forceFocusBlockEnding(nextEditorState, nextContentBlock);
        }
        return nextEditorState;
    }

    handleReturn = (_event: any, editorState: EditorState): DraftHandleValue => {
        let nextEditorState = DraftJSUtils.splitBlockWithData(editorState);
        const currentBlock: ContentBlock = DraftJSUtils.getSelectedBlock(nextEditorState);
        const previousBlock = nextEditorState.getCurrentContent().getBlockBefore(currentBlock.getKey());
        if(currentBlock.getText().length === 0) {
            nextEditorState =
                this.setEditorStateTextForSelection(nextEditorState, DraftJSUtils.emptyBlockValue);
        }
        if (previousBlock.getText().length === 0) {
            const selection = new SelectionState({
                anchorKey: previousBlock.getKey(),
                anchorOffset: 0,
                focusKey: previousBlock.getKey(),
                focusOffset: 0,
            });
            const newContent = Modifier.insertText(
                nextEditorState.getCurrentContent(),
                selection,
                DraftJSUtils.emptyBlockValue,
                currentBlock.getInlineStyleAt(0),
            );
            nextEditorState = EditorState.push(nextEditorState, newContent, "insert-characters");
            nextEditorState = DraftJSUtils.forceFocusBeginningOfSelectionState(nextEditorState, currentBlock.getKey());
        }
        if (nextEditorState) {
            this.onChange(nextEditorState);
            return "handled";
        }
        return "not-handled";
    }

    handleDrop = (): DraftHandleValue => {
        // Considering all drops onto editor handled to prevent the insertion of text when a new
        // widget is dropped on top of an existing ETW
        return "handled";
    }

    // The documentation says that html may be undefined but we can't have options params before required.
    handlePastedText = (text: string, _html: string = "", editorState: EditorState): DraftHandleValue => {
        // We need to handle pasted input just like regular user input.
        // In the future we'll need to parse styles and apply them to editor state.
        const {fontProvider, styleOverrides} = this.props;
        
        // Content Block where pasting of text was happening
        const currentBlock: ContentBlock = DraftJSUtils.getSelectedBlock(editorState);

        const santizedBlocks: BlockMap = DraftJSUtils.sanitizeTextToBlockMap(text)
        let nextEditorState = DraftJSUtils.insertBlockMapFragmet(editorState, santizedBlocks);

        // 1) Save active selection for usage after applying styles
        const selectionStateBeforeInsert = editorState.getSelection();

        // 2) Selection state after insert of fragment
        const selectionStateAfterInsert = nextEditorState.getSelection();

        const selectionOfInsertedText = new SelectionState({
            anchorKey: selectionStateBeforeInsert.getAnchorKey(),
            anchorOffset: selectionStateBeforeInsert.getAnchorOffset(),
            focusKey: selectionStateAfterInsert.getFocusKey(),
            focusOffset: selectionStateAfterInsert.getFocusOffset(),
        });
        
        // 3) Select inserted fragment blocks for the style applying to the current selection of EditorState
        nextEditorState = EditorState.acceptSelection(
            nextEditorState,
            selectionOfInsertedText,
        );
        
        // 4) Apply fontMetadata to the inserted content blocks
        nextEditorState = DraftJSUtils.setFontMetaData(nextEditorState, fontProvider);

        // 5) Line height and text-align style props applying to the inserted content blocks 
        // from content state selection state BEFORE insert
        const currentBlockDataStyles = DraftJSUtils.getSelectedBlock(editorState).getData();
        let nextContentState = Modifier.setBlockData(nextEditorState.getCurrentContent(), selectionOfInsertedText, currentBlockDataStyles);
        
        // 6) Apply inline styles (font size, color and etc) to the inserted content blocks from pasted cursor position block
        const blockHasText = currentBlock.getText().length > 0;
        const currentInlineStyles = blockHasText ? currentBlock.getInlineStyleAt(selectionStateBeforeInsert.getAnchorOffset() - 1) : styleOverrides;

        currentInlineStyles.toArray().forEach(style => {
            nextContentState = Modifier.applyInlineStyle(nextContentState, selectionOfInsertedText, style)
        });
        nextEditorState = EditorState.push(nextEditorState, nextContentState, 'change-block-data');
        
        // 7) Revert back selection
        nextEditorState = EditorState.acceptSelection(
            nextEditorState,
            selectionStateAfterInsert,
        );
        this.onChange(nextEditorState);
        return "handled";
    }

    componentDidUpdate(prevProps: TextEditorWidgetProps) {
        const {isSelected, hasFakeHighlight, editorState} = this.props;
        const isSelectedChanged = isSelected !== prevProps.isSelected;
        const hasFakeHighlightChanged = hasFakeHighlight !== prevProps.hasFakeHighlight;
        if((!isSelected && isSelectedChanged) || (!hasFakeHighlight && hasFakeHighlightChanged)) {
            this.onChange(DraftJSUtils.removeInlineStyle(editorState, DraftStyles.HIGHLIGHT));
        }

        if(isSelected && hasFakeHighlight && hasFakeHighlightChanged) {
            this.onChange(DraftJSUtils.applyInlineStyle(editorState, DraftStyles.HIGHLIGHT));
        }
    }

    render() {
        const {
            editorState,
            readOnly,
            setRef
        } = this.props;

        return (
            <Editor
                editorState={editorState}
                onChange={this.onChange}
                handleKeyCommand={this.handleKeyCommand}
                handleDrop={this.handleDrop}
                customStyleFn={customStyleFn}
                customStyleMap={customInlineStyleMap}
                blockStyleFn={blockStyleFn}
                placeholder={"Your text here..."}
                handleReturn={this.handleReturn}
                handleBeforeInput={this.handleBeforeInput}
                readOnly={readOnly}
                handlePastedText={this.handlePastedText}
                stripPastedStyles={true}
                ref={setRef}
            />
        );
    }
}

// noinspection JSUnusedGlobalSymbols
export {
    TextEditor as default,
    TextEditor
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textEnhancedWidget/TextEditor.tsx